import { loadGoogleMapsScript } from './googleMapsLoader';

export type NearestPoliceStationDetail = {
    name: string;
    address: string;
    phone: string;
};

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const p1 = (lat1 * Math.PI) / 180;
    const p2 = (lat2 * Math.PI) / 180;
    const dp = ((lat2 - lat1) * Math.PI) / 180;
    const dl = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dp / 2) * Math.sin(dp / 2) +
        Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) * Math.sin(dl / 2);
    return 2 * R * Math.asin(Math.sqrt(Math.min(1, a)));
}

function pickClosest(
    lat: number,
    lng: number,
    results: google.maps.places.PlaceResult[]
): google.maps.places.PlaceResult | null {
    let best: google.maps.places.PlaceResult | null = null;
    let bestD = Infinity;
    for (const r of results) {
        const loc = r.geometry?.location;
        if (!loc) continue;
        const d = haversineMeters(lat, lng, loc.lat(), loc.lng());
        if (d < bestD) {
            bestD = d;
            best = r;
        }
    }
    return best;
}

/**
 * Finds the closest `police`-type place to a coordinate (Pakistan / any region).
 * Uses Nearby Search, then Text Search fallback.
 */
export async function fetchNearestPoliceStation(
    apiKey: string,
    lat: number,
    lng: number
): Promise<NearestPoliceStationDetail | null> {
    const key = apiKey.trim();
    if (!key || Number.isNaN(lat) || Number.isNaN(lng)) return null;

    await loadGoogleMapsScript(key);
    const svc = new google.maps.places.PlacesService(document.createElement('div'));
    const origin = new google.maps.LatLng(lat, lng);

    const nearbyResults = await new Promise<google.maps.places.PlaceResult[]>((resolve) => {
        svc.nearbySearch(
            {
                location: origin,
                radius: 20000,
                type: 'police',
            },
            (results, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
                    resolve(results as google.maps.places.PlaceResult[]);
                } else {
                    resolve([]);
                }
            }
        );
    });

    let candidate = pickClosest(lat, lng, nearbyResults);

    if (!candidate) {
        const textResults = await new Promise<google.maps.places.PlaceResult[]>((resolve) => {
            svc.textSearch(
                {
                    query: 'police station',
                    location: origin,
                    radius: 20000,
                },
                (results, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
                        resolve(results as google.maps.places.PlaceResult[]);
                    } else {
                        resolve([]);
                    }
                }
            );
        });
        candidate = pickClosest(lat, lng, textResults);
    }

    const placeId = candidate?.place_id;
    if (!placeId) return null;

    const place = await new Promise<google.maps.places.PlaceResult | null>((resolve) => {
        svc.getDetails(
            {
                placeId,
                fields: [
                    'name',
                    'formatted_address',
                    'formatted_phone_number',
                    'international_phone_number',
                    'geometry',
                ],
            },
            (p, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && p) resolve(p);
                else resolve(null);
            }
        );
    });

    if (!place) return null;

    const phone =
        place.formatted_phone_number || place.international_phone_number || '';
    return {
        name: place.name || 'Police station',
        address: place.formatted_address || '',
        phone: phone.trim(),
    };
}
